import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Facebook, Camera, ArrowRight } from "lucide-react";
import { PHOTOS, Lightbox } from "../components/RealPhotos";
import { EstimateSection } from "../components/Sections";
import { PageHero } from "./ServicePage";
import { BIZ } from "../data/site";
import { Seo } from "../components/Seo";

const TABS = ["All", "Roofing", "Siding", "Gutters", "Crew"];

const Empty = ({ tab }) => (
  <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center" data-testid="gallery-empty">
    <Camera className="mx-auto h-10 w-10 text-slate-400" />
    <p className="font-display mt-4 text-2xl font-bold uppercase text-slate-900">{tab} photos coming soon</p>
    <p className="mx-auto mt-2 max-w-sm text-sm text-slate-600">We're adding new {tab.toLowerCase()} projects. Want to see similar work? Ask us during your free estimate.</p>
    <Link to="/get-quote" className="btn-amber mt-6" data-testid="gallery-empty-quote">Get a free quote <ArrowRight className="h-4 w-4" /></Link>
  </div>
);

export default function Gallery() {
  const [tab, setTab] = useState("All");
  const [open, setOpen] = useState(null);
  const list = tab === "All" ? PHOTOS : PHOTOS.filter((p) => p.c.includes(tab));
  const count = (t) => (t === "All" ? PHOTOS.length : PHOTOS.filter((p) => p.c.includes(t)).length);

  return (
    <main data-testid="gallery-page">
      <Seo title="Roofing, Siding & Gutter Project Photos | Good Home Improvement" description="Real before and after photos of roof replacements, siding and seamless gutter jobs by Good Home Improvement LLC across Milwaukee and Waukesha County." path="/gallery" />
      <PageHero img="/img/fb/crew-on-roof.jpg" crumb="Gallery" kicker="Project gallery" lines={["Our work,", <span key="a" className="text-slate-300">up close.</span>]} sub="Real photos from our jobsites across southeastern Wisconsin." testid="gallery-hero" />
      <section className="bg-white py-16 md:py-24">
        <div className="wrap">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2" role="tablist" data-testid="gallery-tabs">
              {TABS.map((t) => (
                <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`gallery-tab-${t.toLowerCase()}`}
                  className={`rounded-full border px-5 py-2.5 text-sm font-bold transition-[background-color,color,border-color] duration-200 ${tab === t ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 text-slate-800 hover:border-slate-900"}`}>
                  {t} <span className={tab === t ? "text-slate-300" : "text-slate-500"}>({count(t)})</span>
                </button>
              ))}
            </div>
            <a href={BIZ.facebook} target="_blank" rel="noreferrer" className="btn-ghost-dark" data-testid="gallery-facebook-link"><Facebook className="h-4 w-4" />More on Facebook</a>
          </div>
          <motion.div layout className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4" data-testid="gallery-grid">
            <AnimatePresence initial={false}>
              {list.map((p, i) => (
                <motion.button layout key={p.f} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.94 }} transition={{ duration: 0.3 }}
                  onClick={() => setOpen(i)} className="group relative aspect-square overflow-hidden rounded-2xl bg-slate-100" data-testid={`gallery-photo-${p.f}`}>
                  <img src={p.src} alt={p.t} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <span className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-transparent to-transparent" />
                  <span className="absolute bottom-3 left-3 right-3 text-left">
                    <span className="block text-sm font-semibold text-white">{p.t}</span>
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-300">{p.c.join(" · ")}</span>
                  </span>
                </motion.button>
              ))}
            </AnimatePresence>
            {list.length === 0 && <Empty tab={tab} />}
          </motion.div>
        </div>
      </section>
      <AnimatePresence>{open !== null && <Lightbox i={open} setI={setOpen} list={list} />}</AnimatePresence>
      <EstimateSection title={["Want results", "like these?"]} />
    </main>
  );
}
